import type { ParsedUrlQuery } from 'querystring';

import { parseUrlFacets } from 'src/adapters/pageURLToStore/parseUrlFacets';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { PageType } from 'src/types/navigation';
import { getPageFromParam } from 'src/utils/queryParams';
import type { SyncProductsQueryFromURLPayload } from '../types/ProductsQueryState';

const ATTRIBUTES_SHORT_PREFIX = 'a.';
const ATTRIBUTES_FULL_PREFIX = 'attributes.';

const getFirstValue = (value: string | string[] | undefined): string | undefined =>
  Array.isArray(value) ? value[0] : value;

const expandFacetKey = (shortKey: string): string =>
  shortKey.startsWith(ATTRIBUTES_SHORT_PREFIX)
    ? `${ATTRIBUTES_FULL_PREFIX}${shortKey.slice(ATTRIBUTES_SHORT_PREFIX.length)}`
    : shortKey;

const parseSortValue = (
  rawSort: string | undefined,
): { descending: boolean; key: string } | null => {
  if (!rawSort) {
    return null;
  }

  const lastDashIndex = rawSort.lastIndexOf('-');

  if (lastDashIndex === -1) {
    return { descending: false, key: expandFacetKey(rawSort) };
  }

  const direction = rawSort.slice(lastDashIndex + 1);

  if (direction !== 'asc' && direction !== 'desc') {
    return { descending: false, key: expandFacetKey(rawSort) };
  }

  return {
    descending: direction === 'desc',
    key: expandFacetKey(rawSort.slice(0, lastDashIndex)),
  };
};

const getQueryFromAsPath = (asPath: string): ParsedUrlQuery => {
  const queryStringIndex = asPath.indexOf('?');
  if (queryStringIndex === -1) {
    return {};
  }

  const queryString = asPath.slice(queryStringIndex + 1).split('#')[0];
  const params = new URLSearchParams(queryString);
  const query: ParsedUrlQuery = {};

  params.forEach((value, key) => {
    const existingValue = query[key];

    if (existingValue === undefined) {
      query[key] = value;
      return;
    }

    if (Array.isArray(existingValue)) {
      existingValue.push(value);
      return;
    }

    query[key] = [existingValue, value];
  });

  return query;
};

/**
 * Builds the `syncFromURL` payload from a full URL + current Redux state.
 */
export function getProductsQuerySyncPayloadFromURL(
  asPath: string,
  state: Pick<ReduxApplicationState, 'navigation' | 'productsQuery'>,
): SyncProductsQueryFromURLPayload;

export function getProductsQuerySyncPayloadFromURL(
  asPath: string,
  query: ParsedUrlQuery,
  state: Pick<ReduxApplicationState, 'navigation' | 'productsQuery'>,
): SyncProductsQueryFromURLPayload;

export function getProductsQuerySyncPayloadFromURL(
  asPath: string,
  queryOrState: ParsedUrlQuery | Pick<ReduxApplicationState, 'navigation' | 'productsQuery'>,
  maybeState?: Pick<ReduxApplicationState, 'navigation' | 'productsQuery'>,
): SyncProductsQueryFromURLPayload {
  const query = maybeState ? (queryOrState as ParsedUrlQuery) : getQueryFromAsPath(asPath);
  const state = (maybeState ?? queryOrState) as Pick<
    ReduxApplicationState,
    'navigation' | 'productsQuery'
  >;

  const urlFacets = parseUrlFacets(
    asPath,
    state.productsQuery.taxonomyAttributeAliases,
    state.productsQuery.surface !== undefined,
  );

  const activeFacetValuesByKey: Record<string, string[]> = {};
  urlFacets.forEach(({ alias, values }) => {
    activeFacetValuesByKey[alias] = values;
  });

  const includeSearchTerm = state.navigation.pageType === PageType.Search;

  return {
    activeFacetValuesByKey,
    activeSort: parseSortValue(getFirstValue(query.sort)),
    page: getPageFromParam(query),
    searchTerm: includeSearchTerm ? getFirstValue(query.term) : undefined,
  };
}
